/**
 * MonitorPane
 *
 * One physical monitor of the agent, rendered as a tile in the
 * multi-pane WebRTC grid. Each pane:
 *   - owns its own <video> element (the per-track MediaStream routes
 *     to it via useWebRTC's `registerVideoRef`),
 *   - shows a small "Monitor N" label so admins can tell tiles apart
 *     when N > 1,
 *   - has its own click handler that normalizes coordinates against
 *     THIS pane's bounding rect (so clicks land on the right pixel
 *     even when the grid is non-uniform) and tags the input with
 *     the pane's track label so the agent routes to the right rect.
 *
 * Why a separate component (not just N <video> elements in WebRTCPage):
 *  - per-pane refs and click handlers, owned by the pane that
 *    knows its track label
 *  - the page renders the grid + delegates per-pane concerns
 *  - tests can render MonitorPane in isolation with a mock parent
 *    callback
 *
 * Touch / keyboard handling still lives in the page (see WebRTCPage)
 * because those need cross-pane state (which pane has focus, what's
 * the active touch mode, etc.). The pane only owns the per-element
 * mouse click — the highest-frequency path, and the one that has to
 * be correct for the input coordinates to land on the right pixel.
 */

import { useCallback, useEffect, useRef } from 'react';
import { Box, Chip } from '@mui/material';
import MonitorIcon from '@mui/icons-material/Monitor';

export interface MonitorPaneProps {
  /** WebRTC track label this pane listens on, e.g. "screen-0". */
  trackLabel: string;
  /** Display index this pane captures (used for the "Monitor N" label). */
  displayIndex: number;
  /** Pixel dimensions hint, e.g. "1920×1080". Optional. */
  sizeHint?: string;
  /** Pane width in the grid (1 = full, 0.5 = half, etc.). For layout hints. */
  flex?: number;
  /** Absolute percentage rect when this pane belongs to a virtual desktop canvas. */
  canvasRect?: { left: number; top: number; width: number; height: number };
  /**
   * Called when the user clicks this pane. The parent is responsible
   * for the actual input dispatch (it knows about role, rate limits,
   * and the focused-pane state for keyboard input). The pane pre-computes
   * the normalized (0..1) coordinates against its own bounding rect so
   * the parent doesn't have to know which DOM element was clicked.
   */
  onClick?: (coords: { x: number; y: number }, button: 'left' | 'right' | 'middle') => void;
  /**
   * Called on mousedown. The parent uses this for the atomic-click
   * pipeline (move + button.down in order on the input chain). The
   * pane emits the same coords object as onClick so the parent's
   * move/down math stays consistent.
   */
  onMouseDown?: (
    coords: { x: number; y: number },
    button: 'left' | 'right' | 'middle',
    buttons: number
  ) => void;
  /**
   * Called on mouseup. The parent uses this to release the held
   * button through the input chain. Coords are passed for symmetry
   * but the agent ignores them on a pure release.
   */
  onMouseUp?: (
    coords: { x: number; y: number },
    button: 'left' | 'right' | 'middle',
    buttons: number
  ) => void;
  /**
   * Called on mousemove. The parent throttles these (60fps RAF) and
   * sends `move` events to the agent. The pane pre-computes coords.
   */
  onMouseMove?: (coords: { x: number; y: number }, buttons: number) => void;
  /** A mouse gesture was cancelled by the browser (for example, lost capture). */
  onMouseCancel?: (coords: { x: number; y: number }, buttons: number) => void;
  /**
   * Called when the user mouses into this pane. The parent uses this to
   * mark the pane as the active one (so subsequent keyboard events
   * route to this monitor). Optional — the page can also rely on
   * `onClick` alone for "active pane" tracking.
   */
  onFocus?: () => void;
  /**
   * Called once with the pane's <video> element. The parent uses this
   * to bind native touch listeners (passive: false) and to look up
   * the element on `e.target` in other input handlers.
   */
  onVideoRef?: (el: HTMLVideoElement | null) => void;
  /**
   * Registers the pane's <video> with the WebRTC hook so the right
   * MediaStream routes to the right tile. The hook uses the track
   * label as the key (it matches the agent's media.ScreenTrackLabel).
   */
  registerVideoRef: (label: string, el: HTMLVideoElement | null) => void;
  /** Disable interaction (e.g. while the WebRTC session is connecting). */
  disabled?: boolean;
  /** Whether the connection is active — controls the "no signal" overlay. */
  active?: boolean;
}

/**
 * Compute normalized (0..1) coordinates for a mouse event against a
 * <video> element, accounting for the letterbox/pillarbox the browser
 * adds when the video aspect ratio doesn't match the container's.
 *
 * Extracted from the page so the pane owns the math. Returns clamped
 * values in [0, 1] — a click outside the visible video area (on a
 * letterbox bar) snaps to the nearest edge rather than producing
 * negative or >1 values that would land the cursor off-screen.
 */
function normalizeClick(video: HTMLVideoElement, clientX: number, clientY: number) {
  const rect = video.getBoundingClientRect();
  const videoAspect =
    video.videoWidth && video.videoHeight
      ? video.videoWidth / video.videoHeight
      : rect.width / rect.height;
  const containerAspect = rect.width / rect.height;
  let drawWidth: number;
  let drawHeight: number;
  let offsetX = 0;
  let offsetY = 0;
  if (containerAspect > videoAspect) {
    drawHeight = rect.height;
    drawWidth = drawHeight * videoAspect;
    offsetX = (rect.width - drawWidth) / 2;
  } else {
    drawWidth = rect.width;
    drawHeight = drawWidth / videoAspect;
    offsetY = (rect.height - drawHeight) / 2;
  }
  const relX = clientX - rect.left - offsetX;
  const relY = clientY - rect.top - offsetY;
  const x = Math.max(0, Math.min(1, relX / drawWidth));
  const y = Math.max(0, Math.min(1, relY / drawHeight));
  return { x, y };
}

export const MonitorPane: React.FC<MonitorPaneProps> = ({
  trackLabel,
  displayIndex,
  sizeHint,
  flex,
  canvasRect,
  onClick,
  onMouseDown,
  onMouseUp,
  onMouseMove,
  onMouseCancel,
  onFocus,
  onVideoRef,
  registerVideoRef,
  disabled,
  active,
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  // Keep the hook's track-label map in sync with this pane's element.
  // Mount → register, unmount → unregister. We don't unregister on
  // re-renders (the element ref is stable across renders, and the
  // dependency on trackLabel alone means we only re-run when the
  // label changes — at which point React has already torn down the
  // old element via the JSX below).
  useEffect(() => {
    registerVideoRef(trackLabel, videoRef.current);
    return () => {
      registerVideoRef(trackLabel, null);
    };
  }, [registerVideoRef, trackLabel]);

  // Mirror the ref to the page so it can bind touch listeners against
  // this element. We deliberately do this on every render (not via
  // useEffect) so the page sees the freshest ref synchronously — touch
  // handlers bind in a useEffect that runs after this render.
  const setVideoRef = useCallback(
    (el: HTMLVideoElement | null) => {
      videoRef.current = el;
      onVideoRef?.(el);
    },
    [onVideoRef]
  );

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLVideoElement>) => {
      // A physical click already emits down/up. Never inject it twice.
      if (disabled || (onMouseDown && onMouseUp)) return;
      const v = e.currentTarget;
      const coords = normalizeClick(v, e.clientX, e.clientY);
      const button: 'left' | 'right' | 'middle' =
        e.button === 2 ? 'right' : e.button === 1 ? 'middle' : 'left';
      onClick?.(coords, button);
    },
    [disabled, onClick, onMouseDown, onMouseUp]
  );

  const handlePointerDown = useCallback(
    (e: React.PointerEvent<HTMLVideoElement>) => {
      if (disabled || e.pointerType !== 'mouse') return;
      // Do not capture: a drag in the physical desktop canvas can cross
      // monitors, and subsequent events must be retargeted to screen-N.
      onFocus?.();
      const v = e.currentTarget;
      const coords = normalizeClick(v, e.clientX, e.clientY);
      const button: 'left' | 'right' | 'middle' =
        e.button === 2 ? 'right' : e.button === 1 ? 'middle' : 'left';
      onMouseDown?.(coords, button, e.buttons);
    },
    [disabled, onFocus, onMouseDown]
  );

  const handlePointerUp = useCallback(
    (e: React.PointerEvent<HTMLVideoElement>) => {
      if (disabled || e.pointerType !== 'mouse') return;
      const v = e.currentTarget;
      const coords = normalizeClick(v, e.clientX, e.clientY);
      const button: 'left' | 'right' | 'middle' =
        e.button === 2 ? 'right' : e.button === 1 ? 'middle' : 'left';
      onMouseUp?.(coords, button, e.buttons);
    },
    [disabled, onMouseUp]
  );

  const handlePointerMove = useCallback(
    (e: React.PointerEvent<HTMLVideoElement>) => {
      if (disabled || e.pointerType !== 'mouse') return;
      const v = e.currentTarget;
      const coords = normalizeClick(v, e.clientX, e.clientY);
      onMouseMove?.(coords, e.buttons);
    },
    [disabled, onMouseMove]
  );

  const handlePointerCancel = useCallback(
    (e: React.PointerEvent<HTMLVideoElement>) => {
      if (e.pointerType === 'mouse') {
        onMouseCancel?.(normalizeClick(e.currentTarget, e.clientX, e.clientY), e.buttons);
      }
    },
    [onMouseCancel]
  );

  const handleMouseEnter = useCallback(() => {
    if (disabled) return;
    onFocus?.();
  }, [disabled, onFocus]);

  return (
    <Box
      sx={{
        position: 'relative',
        flex: flex ?? 1,
        minWidth: 0,
        minHeight: 0,
        display: 'flex',
        backgroundColor: 'black',
        borderRadius: 1,
        overflow: 'hidden',
        // The pane is the click target for input dispatch — the
        // browser's pointer-events: auto is the default but we
        // surface it explicitly so future refactors don't break it
        // by accident (e.g. by adding an overlay without pointer-
        // events: none).
        cursor: disabled ? 'default' : 'crosshair',
        ...(canvasRect
          ? {
              position: 'absolute',
              left: `${canvasRect.left}%`,
              top: `${canvasRect.top}%`,
              width: `${canvasRect.width}%`,
              height: `${canvasRect.height}%`,
            }
          : {}),
      }}
      data-track-label={trackLabel}
    >
      <video
        ref={setVideoRef}
        autoPlay
        playsInline
        muted
        onClick={handleClick}
        onPointerDown={handlePointerDown}
        onPointerUp={handlePointerUp}
        onPointerMove={handlePointerMove}
        onPointerCancel={handlePointerCancel}
        onMouseEnter={handleMouseEnter}
        onContextMenu={(event) => event.preventDefault()}
        onAuxClick={(event) => event.preventDefault()}
        // PR 3: tap = click for touch devices. We keep the touch
        // listeners (move / end / multi-finger) at the page level so
        // they can coordinate with the global touch-mode state
        // (direct vs trackpad) without the pane having to know about
        // it. The click handler above covers the simple tap case.
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          display: 'block',
          pointerEvents: disabled ? 'none' : 'auto',
        }}
      />
      {/* "Monitor N" badge — shows the human label so admins can
          tell tiles apart when N > 1. Sized to stay readable but not
          cover a meaningful portion of the screen. */}
      <Chip
        size="small"
        icon={<MonitorIcon style={{ fontSize: 14 }} />}
        label={`Monitor ${displayIndex + 1}${sizeHint ? ` · ${sizeHint}` : ''}`}
        sx={{
          position: 'absolute',
          top: 6,
          left: 6,
          height: 22,
          fontSize: '0.7rem',
          // The badge must NOT eat clicks meant for the video below.
          // Picker / target / etc. live behind the video's pointer
          // events; the badge is purely visual.
          pointerEvents: 'none',
          backgroundColor: 'rgba(0, 0, 0, 0.55)',
          color: 'rgba(255, 255, 255, 0.92)',
          '& .MuiChip-icon': { color: 'rgba(255, 255, 255, 0.85)' },
        }}
      />
      {!active && (
        // "No signal" placeholder. Visible while the WebRTC
        // handshake is in progress or after a failed track.
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'rgba(255,255,255,0.45)',
            fontSize: '0.85rem',
            pointerEvents: 'none',
          }}
        >
          Sin señal
        </Box>
      )}
    </Box>
  );
};
